import { XCircle } from "lucide-react";

import { FilePreview } from "@/components/FilePreview";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { stripBundlePrefix } from "@/utils/bundle-name";

import { FileWithMetadata } from "./types";

type DocumentPreviewPanelProps = {
  selectedFile: FileWithMetadata | null;
  selectedClassification: string;
  pageRange: string;
  fileUrl: string | null;
  fileType: "pdf" | "image" | "other" | null;
  isLoading: boolean;
  fileLoadError: boolean;
  errorMessage: string | null;
  isSaving: boolean;
  hasChanges: boolean;
  onRetry?: () => void;
  onAccept: () => void;
};

export const DocumentPreviewPanel = ({
  selectedFile,
  selectedClassification,
  pageRange,
  fileUrl,
  fileType,
  isLoading,
  fileLoadError,
  errorMessage,
  isSaving,
  hasChanges,
  onRetry,
  onAccept,
}: DocumentPreviewPanelProps) => {
  if (!selectedFile) {
    return (
      <div className="flex flex-1 items-center justify-center bg-background-muted">
        <p className="text-foreground-muted">Select a document to preview</p>
      </div>
    );
  }

  return (
    <div className="grid h-full min-h-0 grid-rows-[1fr_auto] gap-4">
      <div className="grid min-h-0 grid-rows-[auto_1fr] gap-4 overflow-hidden">
        <div className="space-y-4">
          <div>
            <h3 className="font-medium">Document Preview</h3>
            <p className="text-sm text-foreground-muted">
              Select document in left side to preview here.
            </p>
          </div>

          <div className="flex items-center gap-4">
            <span
              className="inline-block max-w-[220px] truncate text-sm font-semibold"
              title={stripBundlePrefix(selectedFile.filename)}
            >
              {stripBundlePrefix(selectedFile.filename)}
            </span>

            <p className="space-x-2 text-xs">
              <span>
                {selectedClassification !== selectedFile.classification ? (
                  <>
                    <span className="text-secondary">{selectedClassification}</span>{" "}
                    <span className="text-foreground-muted">
                      (Preview: {selectedFile.classification})
                    </span>
                  </>
                ) : (
                  selectedClassification
                )}
              </span>{" "}
              .<span>{pageRange}</span>
            </p>
          </div>

          {errorMessage && (
            <Alert variant="destructive">
              <XCircle className="mr-2 h-4 w-4" />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}
        </div>

        <div className="relative min-h-0 overflow-auto">
          <FilePreview
            fileUrl={fileUrl}
            fileType={fileType}
            filename={selectedFile.name || selectedFile.filename}
            isLoading={isLoading}
            fileLoadError={fileLoadError}
            onRetry={onRetry}
          />
        </div>
      </div>

      <div className="flex-shrink-0">
        <Button className="w-full" onClick={onAccept} isLoading={isSaving}>
          {isSaving
            ? "Saving"
            : hasChanges
              ? "Save & Close"
              : "Keep in Miscellaneous"}
        </Button>
      </div>
    </div>
  );
};
